// Smart CMS role permissions, stored per role in roles.cms_permissions

import type { KBStore, ArticleNode } from "../articles/types"
import { articleState, nodeTitle } from "../articles/types"
import type { ServiceStore } from "../service-map/types"
import type { SiteConfigStore } from "../site-config/types"

export type CmsSection = "articles" | "serviceMap" | "siteConfig"

// none: hidden. view: read only. edit: create and change drafts. manage: also publish, delete and bulk reset
export type CmsAccess = "none" | "view" | "edit" | "manage"

// all: agent asks before every change. risky: asks before updates, deletes and reverts. optional: may turn on auto-accept
export type CmsReview = "all" | "risky" | "optional"

export interface CmsPermissions {
  articles: CmsAccess
  serviceMap: CmsAccess
  siteConfig: CmsAccess
  agent: {
    use: boolean
    review: CmsReview
    configure: boolean // edit team skills and the document library
  }
}

export const CMS_SECTIONS: { id: CmsSection; label: string }[] = [
  { id: "articles", label: "Articles" },
  { id: "serviceMap", label: "Service Map" },
  { id: "siteConfig", label: "Site Configuration" },
]

export const CMS_ACCESS_LEVELS: { id: CmsAccess; label: string; hint: string }[] = [
  { id: "none", label: "None", hint: "Section is hidden" },
  { id: "view", label: "View", hint: "Read only" },
  { id: "edit", label: "Edit", hint: "Create and edit drafts; published content is locked" },
  { id: "manage", label: "Manage", hint: "Also publish, unpublish, delete and reset" },
]

export const CMS_REVIEW_LEVELS: { id: CmsReview; label: string; hint: string }[] = [
  { id: "all", label: "Every change", hint: "The agent asks before every write" },
  { id: "risky", label: "Risky changes", hint: "The agent asks before updates, deletes and reverts" },
  { id: "optional", label: "Can auto-accept", hint: "May switch on auto-accept" },
]

export const NO_CMS_ACCESS: CmsPermissions = {
  articles: "none",
  serviceMap: "none",
  siteConfig: "none",
  agent: { use: false, review: "all", configure: false },
}

export const FULL_CMS_ACCESS: CmsPermissions = {
  articles: "manage",
  serviceMap: "manage",
  siteConfig: "manage",
  agent: { use: true, review: "optional", configure: true },
}

const RANK: Record<CmsAccess, number> = { none: 0, view: 1, edit: 2, manage: 3 }

export function atLeast(access: CmsAccess, min: CmsAccess): boolean {
  return RANK[access] >= RANK[min]
}

export function hasAnyCmsAccess(p: CmsPermissions): boolean {
  return CMS_SECTIONS.some(s => atLeast(p[s.id], "view"))
}

function asAccess(v: unknown): CmsAccess {
  return v === "view" || v === "edit" || v === "manage" ? v : "none"
}

// Returns null when the role has no cms_permissions (column missing or never set)
export function parseCmsPermissions(raw: unknown): CmsPermissions | null {
  if (!raw || typeof raw !== "object") return null
  const r = raw as Record<string, any>
  const agent = r.agent && typeof r.agent === "object" ? r.agent : {}
  return {
    articles: asAccess(r.articles),
    serviceMap: asAccess(r.serviceMap),
    siteConfig: asAccess(r.siteConfig),
    agent: {
      use: agent.use === true,
      review: agent.review === "risky" || agent.review === "optional" ? agent.review : "all",
      configure: agent.configure === true,
    },
  }
}

// ── Write checks ─────────────────────────────────────────────────────────────
// Each takes the store before and after a write and returns why it isn't allowed, or null

function withoutKeys(v: unknown, keys: string[]): unknown {
  if (!v || typeof v !== "object") return v
  const out: Record<string, unknown> = { ...(v as Record<string, unknown>) }
  for (const k of keys) delete out[k]
  return out
}

function same(a: unknown, b: unknown, ignore: string[] = ["updatedAt"]): boolean {
  return JSON.stringify(withoutKeys(a, ignore)) === JSON.stringify(withoutKeys(b, ignore))
}

function deletedIn(prev: Record<string, unknown>, next: Record<string, unknown>): string | undefined {
  for (const id of Object.keys(prev)) if (!(id in next)) return id
  return undefined
}

const SECTION_LABEL: Record<CmsSection, string> = {
  articles: "Articles",
  serviceMap: "the Service Map",
  siteConfig: "Site Configuration",
}

function baseCheck(section: CmsSection, access: CmsAccess): string | null | "manage" {
  if (access === "manage") return "manage"
  if (!atLeast(access, "edit")) return `You have view-only access to ${SECTION_LABEL[section]}.`
  return null
}

const onlyManagers = (what: string) => `${what} Only a manager can do that.`

export function checkArticlesWrite(prev: KBStore, next: KBStore, access: CmsAccess): string | null {
  const base = baseCheck("articles", access)
  if (base === "manage") return null
  if (base) return base

  for (const bucket of ["categories", "sections", "articles"] as const) {
    if (deletedIn(prev[bucket], next[bucket])) return onlyManagers("This would delete content.")
  }

  for (const [id, after] of Object.entries(next.articles)) {
    const before = prev.articles[id] as ArticleNode | undefined
    if (before && same(before, after)) continue
    const title = nodeTitle(after, next.defaultLocale, next.defaultLocale)
    const locales = new Set([...Object.keys(before?.translations ?? {}), ...Object.keys(after.translations)])
    let anyLiveBefore = false
    for (const loc of locales) {
      const was = before ? articleState(before, loc) : "draft"
      const now = articleState(after, loc)
      if (was === "published") anyLiveBefore = true
      if (was !== now) return onlyManagers(`"${title}" would be ${now === "published" ? "published" : "unpublished"} in ${loc}.`)
      if (was === "published" && !same(before?.translations[loc], after.translations[loc])) {
        return onlyManagers(`"${title}" is published in ${loc}.`)
      }
    }
    // Moving or relabelling a live article changes the live site; reordering alone doesn't
    if (before && anyLiveBefore && !same(before, after, ["updatedAt", "translations", "position"])) {
      return onlyManagers(`"${title}" is published.`)
    }
  }
  return null
}

type LiveService = { status?: string; translations?: Record<string, { status?: string }> }

// Live when the service or any of its locales is published
export function isServiceLive(s: LiveService | undefined): boolean {
  if (!s) return false
  return s.status === "published" || Object.values(s.translations ?? {}).some(t => t?.status === "published")
}

const SERVICE_BUCKETS = [
  "languages", "instances", "countries", "regions", "cities", "providerCategories",
  "serviceCategories", "tags", "populations", "accessibility", "providers", "services",
] as const

export function checkServiceWrite(prev: ServiceStore, next: ServiceStore, access: CmsAccess): string | null {
  const base = baseCheck("serviceMap", access)
  if (base === "manage") return null
  if (base) return base

  for (const bucket of SERVICE_BUCKETS) {
    const p = (prev[bucket] ?? {}) as Record<string, unknown>
    const n = (next[bucket] ?? {}) as Record<string, unknown>
    if (deletedIn(p, n)) return onlyManagers("This would delete records.")
  }

  for (const [id, after] of Object.entries(next.services)) {
    const before = prev.services[id]
    if (before && same(before, after)) continue
    if (isServiceLive(before) || isServiceLive(after)) {
      const name = (after as any).translations?.[next.defaultLocale]?.name || (after as any).name || id
      return onlyManagers(`"${name}" is published, or would be.`)
    }
  }
  return null
}

export function checkSiteConfigWrite(prev: SiteConfigStore, next: SiteConfigStore, access: CmsAccess): string | null {
  const base = baseCheck("siteConfig", access)
  if (base === "manage") return null
  if (base) return base

  if (deletedIn(prev.sites, next.sites) || deletedIn(prev.pageContents, next.pageContents)) {
    return onlyManagers("This would delete site content.")
  }
  return null
}

// ── Agent tools ──────────────────────────────────────────────────────────────

// Tools that only read. Anything not listed is treated as a write
const READ_ONLY_TOOLS = new Set([
  "cms_get_tree", "cms_get_article", "cms_search", "cms_list_children", "cms_list_labels",
  "cms_find_articles", "cms_history", "cms_article_versions", "cms_list_locales",
  "cms_missing_translations", "cms_audit",
  "svc_overview", "svc_search", "svc_list", "svc_get", "svc_import_inspect", "svc_import_plan",
  "svc_geocode", "svc_list_locales", "svc_missing_translations", "svc_get_translations",
  "svc_audit", "svc_schema_reference", "svc_history", "svc_entity_versions",
  "siteconfig_overview", "siteconfig_search", "siteconfig_list", "siteconfig_get",
  "siteconfig_pagecontent_field_guide", "siteconfig_history", "siteconfig_entity_versions",
  // Shared by every section; the filter tools only change what's on screen
  "workflow_review_queue", "workflow_record_details", "workflow_show_changes", "workflow_get_filters",
  "workflow_set_filters", "workflow_save_view", "media_library",
  "corpus_list", "corpus_tree", "corpus_grep", "corpus_read", "corpus_outline",
  "web_search", "web_fetch_page",
])

// Tools that already ask for approval themselves when auto-accept is off
const SELF_GATED_TOOLS = new Set([
  "cms_update_content", "cms_delete", "cms_restore_version", "cms_revert_change",
  "svc_update", "svc_import_apply", "svc_copy_locale", "svc_delete", "svc_restore_version",
  "svc_revert_change",
  "siteconfig_header_menu_remove", "siteconfig_footer_remove_link", "siteconfig_update",
  "siteconfig_delete", "siteconfig_restore_version", "siteconfig_revert_change",
])

export function isReadOnlyTool(name: string): boolean {
  return READ_ONLY_TOOLS.has(name)
}

export function isSelfGatedTool(name: string): boolean {
  return SELF_GATED_TOOLS.has(name)
}
